import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "./AuthContext";
import { getUserCommunities, leaveCommunity } from "../api/communities";

export const CommunityContext = createContext({
  joinedCommunities: [],
  loading: true,
  refresh: () => {},
  leave: async () => {},
});

// Note: there is deliberately no `join` here. Membership is request-based —
// a user sends a join request (see api/joinRequests.js) which an admin
// approves or rejects. Only admins bypass this (they have implicit access
// to every community and are auto-added when they create one).
export default function CommunityProvider({ children }) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setJoinedCommunities([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await getUserCommunities(user.id);
      setJoinedCommunities(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching joined communities:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAuthenticated]);

  const leave = useCallback(
    async (communityId) => {
      await leaveCommunity(user.id, communityId);
      setJoinedCommunities((prev) => prev.filter((c) => String(c.id) !== String(communityId)));
    },
    [user?.id]
  );

  const value = useMemo(
    () => ({ joinedCommunities, loading, refresh, leave }),
    [joinedCommunities, loading, refresh, leave]
  );

  return (
    <CommunityContext.Provider value={value}>{children}</CommunityContext.Provider>
  );
}
